/**
 * UserDashboard
 * Shows portfolio summary, connected exchanges, and quick actions.
 * Summary data is polled live every 15 seconds via /api/portfolio/summary/live.
 */
import { useEffect, useRef, useState } from 'react';
import { useApiClient } from '../api/client';
import type { UserExchange, SyncJobSSEEvent } from '../api/client';
import { StatCards, AssetDistributionChart, ExchangesTable } from '../components/dashboard';
import { useLiveSummary } from '../hooks/useLivePortfolio';

export default function UserDashboard() {
  const api = useApiClient();
  const [exchanges, setExchanges] = useState<UserExchange[]>([]);
  const [exchangesLoading, setExchangesLoading] = useState(true);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Live-priced summary — polls every 15 s automatically
  const { summary, loading: summaryLoading, lastUpdated: _lastUpdated } = useLiveSummary();

  const loading = exchangesLoading || summaryLoading;

  useEffect(() => {
    void api
      .listUserExchanges()
      .then(setExchanges)
      .finally(() => setExchangesLoading(false));
  }, [api]);

  async function handleSync(id: string) {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setSyncingId(id);
    setExchanges((prev) => prev.map((e) => (e.id === id ? { ...e, syncStatus: 'syncing' } : e)));
    try {
      const { syncJobId } = await api.syncExchange(id);
      await api.streamSyncJob(
        id,
        syncJobId,
        (event: SyncJobSSEEvent) => {
          setExchanges((prev) =>
            prev.map((e) =>
              e.id === id
                ? {
                    ...e,
                    syncStatus: event.status as UserExchange['syncStatus'],
                    syncErrorMsg: event.errorMsg,
                    lastSyncedAt: event.completedAt ?? e.lastSyncedAt,
                  }
                : e,
            ),
          );
        },
        ctrl.signal,
      );
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
    } finally {
      setSyncingId(null);
    }
  }

  const pieData = summary
    ? Object.entries(summary.byAssetType).map(([name, value]) => ({ name, value }))
    : [];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <StatCards summary={summary} exchanges={exchanges} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AssetDistributionChart data={pieData} />
        <ExchangesTable
          exchanges={exchanges}
          syncingId={syncingId}
          onSync={(id) => void handleSync(id)}
        />
      </div>
    </div>
  );
}
